import { Box, Button, Card, Form, FormGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { FinanceSearchField } from '@common/enums/financeSearchField';
import type { ValidationErrors } from '@frontend/utils';
import { CarePathType } from '@common/enums/carePathType';
import { financingFlexSearchFilterValidationSchema } from '@common/validation/financingFlex';
import { getSelectedLanguage } from '@frontend/translate';
import { emptyValidationErrors, updateValidationErrorsForMany, validate } from '@frontend/utils';
import { formatDateIntoString } from '@frontend/utils/formatDateIntoString';
import type { BatchRun, FinancingFlexSearchInputFilters } from './financingFlexSearchInputFilters';
import { BatchRunFilter } from '../financingSearchFilters/BatchRunFilter';
import { PeriodFilter } from '../financingSearchFilters/PeriodFilter';
import { SearchForFilter } from '../financingSearchFilters/SearchForFilter';
import { ServiceFilter } from '../financingSearchFilters/ServiceFilter';
import { constructSearchForFilter } from '../searchHelperFunctions';

type FinancingFlexSearchFiltersProps = {
    disableSearch: boolean;
    financingSearchFilters: FinancingFlexSearchInputFilters;
    onChangeFinancingSearchFilters: (filters: FinancingFlexSearchInputFilters) => void;
    onSearch: () => Promise<void>;
};

export function FinancingFlexSearchFilters({
    disableSearch,
    financingSearchFilters,
    onSearch,
    onChangeFinancingSearchFilters,
}: FinancingFlexSearchFiltersProps) {
    const translate = getSelectedLanguage();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    useEffect(() => {
        validateSearchFor(financingSearchFilters.searchText, financingSearchFilters.searchField);
    }, [financingSearchFilters.searchText, financingSearchFilters.searchField]);

    return (
        <Card>
            <Card.Body>
                <Form
                    onSubmit={handleSubmit}
                    role="search"
                    submitOnEnter={true}
                >
                    <Box maxWidth={'900px'}>
                        <FormGroup label={translate.common.processesCreatedBetween}>
                            <PeriodFilter
                                endDate={financingSearchFilters.endDate}
                                errors={errors}
                                onChangeDate={(name, value) => handleSetFilter({ [name]: value })}
                                onValidate={(name, value) =>
                                    handleValidatePeriod(name as keyof FinancingFlexSearchInputFilters, value)
                                }
                                startDate={financingSearchFilters.startDate}
                            />
                        </FormGroup>
                        <SearchForFilter
                            errors={errors}
                            onChangeSearchField={(value) => handleSetFilter({ searchField: value })}
                            onChangeSearchText={(value) => handleSetFilter({ searchText: value })}
                            searchField={financingSearchFilters.searchField}
                            searchText={financingSearchFilters.searchText}
                        />
                        <ServiceFilter
                            carePathType={CarePathType.flex}
                            onChange={({ episodeCategoryId, serviceId }) =>
                                handleSetFilter({ episodeCategoryId, serviceId })
                            }
                            selectedValue={financingSearchFilters.episodeCategoryId || financingSearchFilters.serviceId}
                        />
                        <BatchRunFilter
                            batchRun={financingSearchFilters.batchRun}
                            errors={errors}
                            onChange={handleBatchRunFilterChange}
                        />
                        <Box
                            display="flex"
                            justifyContent="space-between"
                        >
                            <Button
                                color="primary"
                                isDisabled={disableSearch || !errors.isEmpty()}
                                type="submit"
                            >
                                {translate.common.searchRefresh}
                            </Button>
                        </Box>
                    </Box>
                </Form>
            </Card.Body>
        </Card>
    );

    function handleSetFilter(filter: Partial<FinancingFlexSearchInputFilters>) {
        onChangeFinancingSearchFilters({ ...financingSearchFilters, ...filter });
    }

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        // validate everything as a last sanity check
        const { errors: newErrors } = validate(financingFlexSearchFilterValidationSchema, {
            ...financingSearchFilters,
            ...constructSearchForFilter(financingSearchFilters.searchText, financingSearchFilters.searchField),
            endDate: formatPeriodDateToString(financingSearchFilters.endDate),
            startDate: formatPeriodDateToString(financingSearchFilters.startDate),
        });

        setErrors(newErrors);

        if (!newErrors.isEmpty()) {
            return;
        }

        await onSearch();
    }

    function handleValidatePeriod(filter: keyof FinancingFlexSearchInputFilters, value: Date | null) {
        const object = {
            endDate: formatPeriodDateToString(filter === 'endDate' ? value : financingSearchFilters.endDate),
            startDate: formatPeriodDateToString(filter === 'startDate' ? value : financingSearchFilters.startDate),
        };

        const newErrors = updateValidationErrorsForMany(financingFlexSearchFilterValidationSchema, object, errors, [
            'startDate',
            'endDate',
        ]);

        setErrors(newErrors);
    }

    function validateSearchFor(searchText?: string, searchField?: FinanceSearchField) {
        let newErrors = errors;

        const searchForFilter = constructSearchForFilter(searchText, searchField);

        if (!searchForFilter.searchField && !searchForFilter.searchText) {
            setErrors(newErrors.remove('searchField').remove('searchText'));

            return;
        }

        newErrors = updateValidationErrorsForMany(
            financingFlexSearchFilterValidationSchema,
            searchForFilter,
            newErrors,
            ['searchText', 'searchField']
        );

        setErrors(newErrors);
    }

    function handleBatchRunFilterChange(batchRun?: BatchRun): void {
        const batchRunFilter = { batchRun };

        handleSetFilter(batchRunFilter);

        const newErrors = updateValidationErrorsForMany(
            financingFlexSearchFilterValidationSchema,
            batchRunFilter,
            errors,
            ['batchRun.batchType', 'batchRun.runNumber']
        );

        setErrors(newErrors);
    }

    function formatPeriodDateToString(date: Date | null | undefined) {
        return formatDateIntoString(date) ?? undefined;
    }
}
